import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, X } from "lucide-react";
import { toast } from "sonner";

export const MAX_SECONDS = 20;
const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
const pickMime = () => MIME_CANDIDATES.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const fmt = (s) => `0:${String(Math.min(MAX_SECONDS, Math.floor(s))).padStart(2, "0")}`;

// Preview player for a freshly recorded (local) clip.
const Preview = ({ url, seconds, onDiscard }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  return (
    <div className="inline-flex items-center gap-2 bg-white rounded-2xl p-1.5 pr-3 border border-[#f9a8c9]" data-testid="cheer-voice-pending">
      <audio ref={audioRef} src={url} onEnded={() => setPlaying(false)} preload="auto" />
      <button type="button" onClick={() => { if (playing) audioRef.current.pause(); else audioRef.current.play(); setPlaying(!playing); }} className="w-9 h-9 rounded-full bg-[#e5356f] text-white flex items-center justify-center" aria-label={playing ? "Pause preview" : "Play preview"} data-testid="cheer-voice-preview-play">{playing ? <Pause size={14} /> : <Play size={14} />}</button>
      <span className="text-xs text-[#7a1f45] font-semibold inline-flex items-center gap-1"><Mic size={12} /> Voice note · {fmt(seconds)}</span>
      <button type="button" onClick={onDiscard} className="text-gray-400 hover:text-[#e5356f]" aria-label="Discard voice note" data-testid="cheer-voice-remove"><X size={14} /></button>
    </div>
  );
};

// Mic button -> records up to 20 s (MediaRecorder) with a live level meter -> onChange({ blob, url, seconds, mime }).
const VoiceRecorder = ({ value, onChange, disabled, title }) => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState([0, 0, 0, 0, 0, 0]);
  const rec = useRef(null);
  const stream = useRef(null);
  const ctx = useRef(null);
  const raf = useRef(null);
  const startedAt = useRef(0);

  const cleanup = () => {
    cancelAnimationFrame(raf.current);
    stream.current?.getTracks().forEach((t) => t.stop());
    ctx.current?.close?.().catch(() => {});
    stream.current = null; ctx.current = null; rec.current = null;
    setRecording(false); setLevels([0, 0, 0, 0, 0, 0]);
  };
  useEffect(() => cleanup, []);

  const stop = () => { if (rec.current?.state === "recording") rec.current.stop(); };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) { toast.error("Voice notes aren't supported in this browser"); return; }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Microphone permission needed", { description: "Allow the microphone in your browser to record a voice note." });
      return;
    }
    const mime = pickMime();
    const chunks = [];
    const r = new MediaRecorder(stream.current, mime ? { mimeType: mime } : undefined);
    rec.current = r;
    r.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    r.onstop = () => {
      const secs = Math.min(MAX_SECONDS, (Date.now() - startedAt.current) / 1000);
      const blob = new Blob(chunks, { type: r.mimeType || mime || "audio/webm" });
      cleanup();
      if (secs < 0.5 || blob.size < 200) { toast.error("That was too short - hold the mic a moment longer"); return; }
      onChange({ blob, url: URL.createObjectURL(blob), seconds: secs, mime: blob.type });
    };
    try {
      ctx.current = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.current.createAnalyser();
      analyser.fftSize = 64;
      ctx.current.createMediaStreamSource(stream.current).connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(buf);
        const step = Math.floor(buf.length / 6);
        setLevels(Array.from({ length: 6 }, (_, i) => Math.min(1, buf.slice(i * step, (i + 1) * step).reduce((a, b) => a + b, 0) / step / 160)));
        const el = (Date.now() - startedAt.current) / 1000;
        setSeconds(el);
        if (el >= MAX_SECONDS) { stop(); return; }
        raf.current = requestAnimationFrame(tick);
      };
      raf.current = requestAnimationFrame(tick);
    } catch { /* level meter is optional */ }
    startedAt.current = Date.now();
    setSeconds(0);
    setRecording(true);
    r.start(250);
    setTimeout(stop, MAX_SECONDS * 1000 + 150);
  };

  if (value) return <Preview url={value.url} seconds={value.seconds} onDiscard={() => { URL.revokeObjectURL(value.url); onChange(null); }} />;

  if (recording) {
    return (
      <div className="inline-flex items-center gap-3 bg-white rounded-2xl px-3 py-1.5 border-2 border-[#e5356f]" data-testid="cheer-voice-recording">
        <span className="w-2.5 h-2.5 rounded-full bg-[#e5356f] animate-pulse" />
        <span className="flex items-end gap-0.5 h-5" aria-hidden="true">{levels.map((l, i) => <span key={i} className="w-1 rounded-full bg-[#e5356f] transition-[height] duration-75" style={{ height: `${Math.max(15, l * 100)}%` }} />)}</span>
        <span className="text-xs font-bold text-[#7a1f45] tabular-nums" data-testid="cheer-voice-timer">{fmt(seconds)} / {fmt(MAX_SECONDS)}</span>
        <button type="button" onClick={stop} className="inline-flex items-center gap-1 rounded-full bg-[#e5356f] hover:bg-[#c92a5e] text-white text-xs font-bold px-3 py-1" data-testid="cheer-voice-stop"><Square size={10} fill="#fff" /> Stop</button>
      </div>
    );
  }

  return (
    <button type="button" onClick={start} disabled={disabled} className="w-10 h-10 rounded-full flex items-center justify-center border-2 border-[#f9a8c9] text-[#e5356f] hover:bg-[#ffe0ec] transition-colors disabled:opacity-50" title={title || "Record a 20-second voice note"} aria-label="Record a voice note" data-testid="cheer-voice-button" data-offline={title ? "true" : "false"}><Mic size={16} /></button>
  );
};

export default VoiceRecorder;
